/**
 * weatherUtils.js
 *
 * Free weather API — no API key required:
 *  - Forecast   : Open-Meteo (up to 16 days ahead)
 *  - Historical : Open-Meteo Archive API (past dates)
 *
 * Coordinates (lat/lng) come directly from data.location — no geocoding needed.
 */

// ─── WMO Weather Interpretation Codes (BM & EN) ────────────────────────────────
const WMO_CODES = {
  // Cerah / Clear
  0: { BM: "Cerah", EN: "Clear Sky", emoji: "☀️" },

  // Berawan / Cloudy
  1: { BM: "Cerah", EN: "Mainly Clear", emoji: "🌤️" },
  2: { BM: "Mendung", EN: "Partly Cloudy", emoji: "⛅" },
  3: { BM: "Mendung", EN: "Overcast", emoji: "☁️" },

  // Kabus / Fog
  45: { BM: "Berkabus", EN: "Foggy", emoji: "🌫️" },
  48: { BM: "Berkabus", EN: "Foggy", emoji: "🌫️" },

  // Hujan Renyai / Drizzle
  51: { BM: "Hujan Renyai", EN: "Light Drizzle", emoji: "🌦️" },
  53: { BM: "Hujan Renyai", EN: "Moderate Drizzle", emoji: "🌦️" },
  55: { BM: "Hujan Renyai", EN: "Heavy Drizzle", emoji: "🌦️" },

  // Hujan / Rain
  61: { BM: "Hujan", EN: "Light Rain", emoji: "🌧️" },
  63: { BM: "Hujan", EN: "Rain", emoji: "🌧️" },

  // Hujan Lebat / Heavy Rain
  65: { BM: "Hujan Lebat", EN: "Heavy Rain", emoji: "🌧️" },

  // Hujan Sekejap / Showers
  80: { BM: "Hujan", EN: "Rain Showers", emoji: "🌦️" },
  81: { BM: "Hujan", EN: "Rain Showers", emoji: "🌧️" },
  82: { BM: "Hujan Lebat", EN: "Heavy Rain Showers", emoji: "⛈️" },

  // Ribut Petir / Thunderstorm
  95: { BM: "Ribut Petir", EN: "Thunderstorm", emoji: "⛈️" },
  96: { BM: "Ribut Petir", EN: "Thunderstorm", emoji: "⛈️" },
  99: { BM: "Ribut Petir", EN: "Thunderstorm", emoji: "⛈️" },
};

/**
 * Resolve a WMO weather code to description + emoji based on language.
 * Falls back gracefully for unknown codes.
 */
export function resolveWeatherCode(code, language = "BM") {
  const langKey = language?.toUpperCase() === "EN" ? "EN" : "BM";
  const item = WMO_CODES[code];
  if (!item) {
    return {
      description: langKey === "EN" ? "Unknown" : "Tidak Diketahui",
      emoji: "🌡️"
    };
  }
  return {
    description: item[langKey] || item.BM,
    emoji: item.emoji
  };
}

// ─── Weather Fetch ─────────────────────────────────────────────────────────────

/**
 * Maximum days ahead that Open-Meteo forecast covers.
 */
const FORECAST_MAX_DAYS = 15;

/**
 * In-memory cache — persists across re-renders within the same page load,
 * but resets on every browser refresh (F5). Unlike sessionStorage, it does
 * NOT survive a page reload.
 */
const weatherCache = new Map();

/**
 * Fetch time-aware hourly weather for a specific date, coordinates, and event start time.
 *
 * @param {number} lat
 * @param {number} lon        — Open-Meteo uses "longitude"; lng from data.location maps here
 * @param {string} dateStr    — "YYYY-MM-DD"
 * @param {string} startTime  — "HH:mm" (e.g. "14:00") — used to target the correct hour
 *
 * Returns one of:
 *   { status: "available", temperature, tempMax, tempMin, precipitationProbability, weatherCode, description, emoji }
 *     temperature            — actual temp at the event's start hour (°C)
 *     precipitationProbability — % rain chance at that hour (forecast only; null for past)
 *   { status: "too-far" }   — event is beyond the 16-day forecast window
 *   { status: "error" }     — network / parse error or missing coords
 */
export async function fetchWeather(lat, lon, dateStr, startTime) {
  if (lat == null || lon == null || !dateStr) return { status: "error" };

  const cacheKey = `weather_${lat}_${lon}_${dateStr}_${startTime ?? "00"}`;
  if (weatherCache.has(cacheKey)) return weatherCache.get(cacheKey);

  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const eventDay = new Date(dateStr);
    eventDay.setHours(0, 0, 0, 0);

    const diffDays = Math.round((eventDay - today) / 86_400_000);

    // Too far in the future — Open-Meteo forecast only covers ~16 days
    if (diffDays > FORECAST_MAX_DAYS) {
      return { status: "too-far" };
    }

    // Parse the event's start hour (fallback: noon)
    const eventHour = startTime ? parseInt(startTime.split(":")[0], 10) : 12;

    // Request both hourly (for event-time data) and daily (for min/max range bar)
    const hourlyParams = "hourly=temperature_2m,weather_code,precipitation_probability";
    const dailyParams = "daily=temperature_2m_max,temperature_2m_min,precipitation_sum";
    const commonParams = `latitude=${lat}&longitude=${lon}&timezone=auto`;

    let apiUrl;
    let isForecast = false;

    if (diffDays >= 0) {
      // Future / today → Forecast API
      isForecast = true;
      apiUrl = `https://api.open-meteo.com/v1/forecast?${commonParams}&${hourlyParams}&${dailyParams}&start_date=${dateStr}&end_date=${dateStr}`;
    } else {
      // Past date → Historical Archive API (precipitation_probability not available)
      apiUrl = `https://archive-api.open-meteo.com/v1/archive?${commonParams}&${hourlyParams}&${dailyParams}&start_date=${dateStr}&end_date=${dateStr}`;
    }

    const res = await fetch(apiUrl);
    if (!res.ok) return { status: "error" };

    const json = await res.json();
    const hourly = json.hourly;
    const daily = json.daily;

    if (!hourly?.time?.length || !daily?.temperature_2m_max?.length) {
      return { status: "error" };
    }

    // Find the hourly index that matches the event's start hour.
    // Open-Meteo returns times like "2026-08-10T14:00" — we match by hour.
    const hourIndex = hourly.time.findIndex(
      (t) => new Date(t).getHours() === eventHour
    );
    // Fallback: treat eventHour as a direct index (index 0 = 00:00, 14 = 14:00)
    const idx = hourIndex !== -1 ? hourIndex : eventHour;

    const temperature = Math.round(hourly.temperature_2m[idx]);
    const weatherCode = hourly.weather_code[idx];
    const precipProb = isForecast
      ? (hourly.precipitation_probability?.[idx] ?? null)
      : null;

    const { description, emoji } = resolveWeatherCode(weatherCode);

    const result = {
      status: "available",
      temperature,                                               // actual temp at event hour
      tempMax: Math.round(daily.temperature_2m_max[0]),         // day's high
      tempMin: Math.round(daily.temperature_2m_min[0]),         // day's low
      precipitationSum: daily.precipitation_sum[0]?.toFixed(1) ?? "0.0",
      precipitationProbability: precipProb,
      weatherCode,
      description,
      emoji,
    };

    // In-memory cache — clears on browser refresh, persists across re-renders
    weatherCache.set(cacheKey, result);
    return result;
  } catch {
    return { status: "error" };
  }
}
